import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { 
  TrendingUp, 
  TrendingDown, 
  BarChart3, 
  Calendar, 
  DollarSign,
  ScanBarcode,
  Layers,
  RefreshCw
} from 'lucide-react';
import api from '../../../utils/api';
import { toast } from 'sonner';

/**
 * AnalyticsTrendCharts - Historical trend visualization
 * 
 * Shows:
 * - Scans over time (line chart)
 * - RSHDs created (area chart)
 * - Revenue/Savings (bar chart)
 * - Week-over-week comparison
 */
const AnalyticsTrendCharts = () => {
  const [loading, setLoading] = useState(true);
  const [trendData, setTrendData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(30);
  const [activeChart, setActiveChart] = useState('scans');

  const fetchTrendData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/barcode/analytics/trends?days=${selectedPeriod}`);
      setTrendData(response.data);
    } catch (error) {
      console.error('Failed to fetch trend data:', error);
      toast.error('Failed to load trend data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrendData();
  }, [selectedPeriod]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border">
          <p className="font-medium text-gray-900">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="h-80 flex items-center justify-center">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  if (!trendData) {
    return (
      <Card>
        <CardContent className="h-80 flex items-center justify-center text-gray-500">
          No trend data available
        </CardContent>
      </Card>
    );
  }

  const { data_points, summary } = trendData;
  const isPositiveTrend = summary.week_over_week_change >= 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 font-medium">Total Scans</p>
                <p className="text-2xl font-bold text-blue-900">{summary.total_scans.toLocaleString()}</p>
                <p className="text-xs text-blue-600">{summary.avg_daily_scans}/day avg</p>
              </div>
              <ScanBarcode className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-emerald-600 font-medium">RSHDs Created</p>
                <p className="text-2xl font-bold text-emerald-900">{summary.total_rshds.toLocaleString()}</p>
                <p className="text-xs text-emerald-600">{summary.avg_daily_rshds}/day avg</p>
              </div>
              <Layers className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600 font-medium">Deal Value</p>
                <p className="text-2xl font-bold text-purple-900">${summary.total_deal_value.toLocaleString()}</p>
                <p className="text-xs text-purple-600">Total posted</p>
              </div>
              <DollarSign className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className={`bg-gradient-to-br ${isPositiveTrend ? 'from-green-50 to-green-100 border-green-200' : 'from-red-50 to-red-100 border-red-200'}`}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs font-medium ${isPositiveTrend ? 'text-green-600' : 'text-red-600'}`}>WoW Change</p>
                <p className={`text-2xl font-bold ${isPositiveTrend ? 'text-green-900' : 'text-red-900'}`}>
                  {isPositiveTrend ? '+' : ''}{summary.week_over_week_change}%
                </p>
                <p className={`text-xs ${isPositiveTrend ? 'text-green-600' : 'text-red-600'}`}>vs last week</p>
              </div>
              {isPositiveTrend ? (
                <TrendingUp className="w-8 h-8 text-green-400" />
              ) : (
                <TrendingDown className="w-8 h-8 text-red-400" />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                Historical Trends
              </CardTitle>
              <CardDescription>Performance over the last {selectedPeriod} days</CardDescription>
            </div>
            
            {/* Period Selector */}
            <div className="flex gap-2">
              {[7, 14, 30, 60].map((period) => (
                <Button
                  key={period}
                  variant={selectedPeriod === period ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedPeriod(period)}
                  data-testid={`period-${period}-btn`}
                >
                  {period}D
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeChart} onValueChange={setActiveChart}>
            <TabsList className="mb-4">
              <TabsTrigger value="scans" data-testid="chart-scans-tab">
                <ScanBarcode className="w-4 h-4 mr-1" />
                Scans
              </TabsTrigger>
              <TabsTrigger value="rshds" data-testid="chart-rshds-tab">
                <Layers className="w-4 h-4 mr-1" />
                RSHDs
              </TabsTrigger>
              <TabsTrigger value="revenue" data-testid="chart-revenue-tab">
                <DollarSign className="w-4 h-4 mr-1" />
                Revenue
              </TabsTrigger>
            </TabsList>

            <TabsContent value="scans" className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data_points}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="day_label" 
                    tick={{ fontSize: 12 }} 
                    tickLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="scans" 
                    name="Total Scans"
                    stroke="#3b82f6" 
                    fill="#93c5fd" 
                    fillOpacity={0.5}
                    strokeWidth={2}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="successful" 
                    name="Successful"
                    stroke="#10b981" 
                    fill="#6ee7b7" 
                    fillOpacity={0.5}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </TabsContent>

            <TabsContent value="rshds" className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data_points}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="day_label" 
                    tick={{ fontSize: 12 }} 
                    tickLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="rshds" 
                    name="Sizzling Hot Deals Created"
                    stroke="#8b5cf6" 
                    strokeWidth={3}
                    dot={{ fill: '#8b5cf6', strokeWidth: 2 }}
                    activeDot={{ r: 6, fill: '#7c3aed' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="success_rate" 
                    name="Success Rate %"
                    stroke="#f59e0b" 
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </TabsContent>

            <TabsContent value="revenue" className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data_points}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="day_label" 
                    tick={{ fontSize: 12 }} 
                    tickLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar 
                    dataKey="deal_value" 
                    name="Deal Value ($)"
                    fill="#10b981" 
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar 
                    dataKey="savings_generated" 
                    name="Savings ($)"
                    fill="#6366f1" 
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsTrendCharts;
